import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import API_BASE from "../config/api";
import { resolveImageUrl, getInitials } from "../utils/siteHelpers";
import useScrollReveal from "../hooks/useScrollReveal";
import "../styles/reveal.css";
import SiteLoadingState from "../components/site/SiteLoadingState";
import SiteErrorState from "../components/site/SiteErrorState";
import SiteEmptyState from "../components/site/SiteEmptyState";

/* ============================================================
   BLANK defaults — a newly activated site shows its OWN (empty)
   content, never demo data. Real data (get_personal_site.php)
   overrides per-field. Do not add placeholder copy here.
   ============================================================ */
const DEMO = {
    name: "",
    currentjob: "",
    slogan: "",
    aboutme: "",
    experience: "",
    languages: "",
    mobile: "",
    whatsapp: "",
    email: "",
    addresses: "",
    aboutimg: "",
    coverimg: "",
    images: [],
};

/* What I Do / Expertise cards — filled by the owner (blank by default) */
const SERVICES = [];

/* Testimonials — blank by default */
const TESTIMONIALS = [];

/* Load Bootstrap (grid + navbar collapse + form controls) once, clean up on
   unmount. Fonts: Sora for headings, Inter for body — no external animation
   library, scroll reveal is handled by the shared CSS-only hook. */
function useCdnAssets() {
    useEffect(() => {
        const added = [];
        const addLink = (href) => {
            if (document.querySelector(`link[href="${href}"]`)) return;
            const l = document.createElement("link");
            l.rel = "stylesheet";
            l.href = href;
            l.dataset.pw = "1";
            document.head.appendChild(l);
            added.push(l);
        };
        const addScript = (src) =>
            new Promise((resolve) => {
                if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
                const s = document.createElement("script");
                s.src = src;
                s.async = true;
                s.dataset.pw = "1";
                s.onload = () => resolve();
                document.body.appendChild(s);
                added.push(s);
            });

        addLink("https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css");
        addLink("https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css");
        addLink("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Sora:wght@600;700;800&display=swap");
        addScript("https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/js/bootstrap.bundle.min.js");

        return () => { added.forEach((n) => { try { n.remove(); } catch (e) { /* noop */ } }); };
    }, []);
}

/* Tracks header scroll position so the navbar can pick up a background
   once the page has scrolled past the hero. */
function useScrolled(threshold = 24) {
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > threshold);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, [threshold]);
    return scrolled;
}

/* Drop-in <img> replacement: if `src` is empty or fails to load, renders a
   flat themed tile with a centered icon instead of a broken image box. */
function ImgOrFallback({ src, alt, className = "", icon = "bi-image", loading = "lazy" }) {
    const [ok, setOk] = useState(true);
    useEffect(() => { setOk(true); }, [src]);
    if (src && ok) {
        return <img src={src} alt={alt} loading={loading} className={className} onError={() => setOk(false)} />;
    }
    return (
        <div className={`pw-img-fallback ${className}`} role="img" aria-label={alt || "Image unavailable"}>
            <i className={`bi ${icon}`} aria-hidden="true"></i>
        </div>
    );
}

export default function PersonalWebsite() {
    const { domain } = useParams();
    const [real, setReal] = useState(null);
    const [realImages, setRealImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [imgOk, setImgOk] = useState(true);
    const [coverOk, setCoverOk] = useState(true);

    // contact form
    const [cName, setCName] = useState("");
    const [cPhone, setCPhone] = useState("");
    const [cEmail, setCEmail] = useState("");
    const [cMsg, setCMsg] = useState("");
    const [fetchToken, setFetchToken] = useState(0);

    useCdnAssets();
    const scrolled = useScrolled();

    const loadSite = useCallback(() => {
        if (!domain) { setLoading(false); return; }
        setLoading(true);
        setLoadError(false);
        fetch(`${API_BASE}/api/get_personal_site.php?domain=${encodeURIComponent(domain)}`)
            .then((r) => r.json())
            .then((res) => {
                if (res.success && res.data) setReal(res.data);
                if (res.images && res.images.length) setRealImages(res.images);
            })
            .catch(() => setLoadError(true))
            .finally(() => setLoading(false));
    }, [domain]);

    useEffect(() => { loadSite(); }, [loadSite, fetchToken]);
    useScrollReveal([loading, real, realImages]);

    const pick = (k) => (real && real[k] && String(real[k]).trim() !== "" ? real[k] : DEMO[k]);
    const d = useMemo(() => ({
        name: pick("name"),
        currentjob: pick("currentjob"),
        slogan: pick("slogan"),
        aboutme: pick("aboutme"),
        experience: pick("experience"),
        languages: pick("languages"),
        mobile: pick("mobile"),
        whatsapp: pick("whatsapp"),
        email: pick("email"),
        addresses: pick("addresses"),
        aboutimg: real ? (real.aboutimg || "") : DEMO.aboutimg,
        coverimg: real ? (real.coverimg || "") : DEMO.coverimg,
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [real]);
    const images = realImages.length ? realImages : DEMO.images;

    // Image links from the API mix relative paths (need API_BASE prefixed)
    // with already-absolute CDN URLs — detect and skip resolution for the
    // latter so they don't get double-prefixed.
    const imgUrl = useCallback((u) => {
        if (!u) return u;
        if (/^https?:\/\//i.test(u) || u.startsWith("//")) return u;
        return resolveImageUrl(API_BASE, u);
    }, []);
    const photo = imgUrl(d.aboutimg);
    const cover = imgUrl(d.coverimg);
    const hasPhoto = photo && imgOk;
    const initials = getInitials(d.name);
    const langCount = d.languages ? d.languages.split(",").filter(Boolean).length : 0;
    const isSiteEmpty = !d.name && !d.aboutme && images.length === 0;

    // Preload the cover image so the hero background only switches on once
    // we know it resolves — otherwise it falls back to the flat theme color.
    useEffect(() => {
        if (!cover) { setCoverOk(false); return; }
        let cancelled = false;
        const img = new Image();
        img.onload = () => { if (!cancelled) setCoverOk(true); };
        img.onerror = () => { if (!cancelled) setCoverOk(false); };
        img.src = cover;
        return () => { cancelled = true; };
    }, [cover]);

    const handleContact = (e) => {
        e.preventDefault();
        const subject = encodeURIComponent(`Website enquiry from ${cName || "Visitor"}`);
        const body = encodeURIComponent(`Name: ${cName}\nPhone: ${cPhone}\nEmail: ${cEmail}\n\n${cMsg}`);
        window.location.href = `mailto:${d.email}?subject=${subject}&body=${body}`;
    };

    if (loading) return <SiteLoadingState theme="personal" label="Loading site…" />;
    if (loadError) return <SiteErrorState theme="personal" onRetry={() => setFetchToken((t) => t + 1)} />;

    return (
        <div className="pw">
            <a href="#main-content" className="pw-skip-link">Skip to content</a>

            {/* NAV */}
            <nav className={`navbar navbar-expand-lg fixed-top py-3 ${scrolled ? "pw-nav-scrolled" : ""}`} aria-label="Primary">
                <div className="container">
                    <a className="navbar-brand fw-bold fs-4" href="#top">{d.name || "My Website"}</a>
                    <button className="navbar-toggler border-0" type="button" data-bs-toggle="collapse" data-bs-target="#navbarContent" aria-controls="navbarContent" aria-label="Toggle navigation menu">
                        <span className="navbar-toggler-icon"></span>
                    </button>
                    <div className="collapse navbar-collapse" id="navbarContent">
                        <ul className="navbar-nav mx-auto mb-2 mb-lg-0 fw-semibold">
                            <li className="nav-item"><a className="nav-link px-3" href="#portfolio">Portfolio</a></li>
                            <li className="nav-item"><a className="nav-link px-3" href="#about">About</a></li>
                            <li className="nav-item"><a className="nav-link px-3" href="#services">What I Do</a></li>
                            <li className="nav-item"><a className="nav-link px-3" href="#reviews">Reviews</a></li>
                        </ul>
                        <a href="#contact" className="btn pw-btn-accent rounded-pill px-4 py-2 fw-semibold d-none d-lg-inline-block">Get in touch</a>
                    </div>
                </div>
            </nav>

            <main id="main-content">
                {/* HERO */}
                <header
                    id="top"
                    className="hero-section"
                    style={cover && coverOk ? { backgroundImage: `linear-gradient(120deg, rgba(20,33,61,.9) 0%, rgba(20,33,61,.72) 100%), url("${cover}")` } : undefined}
                >
                    <div className="container">
                        <div className="row align-items-center g-5">
                            <div className="col-lg-7" data-reveal="fade-up">
                                <span className="pw-badge">Dial Nepal member</span>
                                {d.currentjob && <p className="pw-role mt-3 mb-0">{d.currentjob}</p>}
                                <h1 className="display-4 fw-bold mt-2 mb-4">{d.name || "Welcome to my website"}</h1>
                                {d.slogan && <p className="lead pw-lead mb-5 pe-lg-5">{d.slogan}</p>}
                                <div className="d-flex gap-3 flex-wrap">
                                    <a href="#portfolio" className="btn pw-btn-accent btn-lg rounded-pill px-4 py-3 fw-bold">View my work</a>
                                    <a href="#contact" className="btn pw-btn-outline btn-lg rounded-pill px-4 py-3 fw-bold">Contact me</a>
                                </div>
                            </div>
                            {/* <div className="col-lg-5 text-center" data-reveal="fade-left">
                                <div className={`hero-photo mx-auto ${hasPhoto ? "" : "hero-photo-ph"}`}>
                                    {hasPhoto ? (
                                        <img src={photo} alt={d.name || "Profile"} loading="lazy" onError={() => setImgOk(false)} />
                                    ) : (
                                        <span aria-hidden="true">{initials}</span>
                                    )}
                                </div>
                                {d.experience && <p className="hero-exp mt-3 mb-0">{d.experience} experience</p>}
                            </div> */}
                        </div>
                    </div>
                </header>

                {isSiteEmpty && (
                    <section className="py-5">
                        <div className="container py-5">
                            <SiteEmptyState
                                icon="bi-brush"
                                title="This website is still being set up"
                                message="Once the owner adds a profile photo, bio and portfolio highlights, they'll appear here."
                            />
                        </div>
                    </section>
                )}

                {/* PORTFOLIO */}
                {images.length > 0 && (
                    <section id="portfolio" className="py-5 mt-3">
                        <div className="container py-4">
                            <div className="mb-5" data-reveal="fade-up">
                                <h2 className="section-title">Portfolio &amp; moments</h2>
                                <p className="text-muted" style={{ maxWidth: 560 }}>A showcase of my work, events and community highlights.</p>
                            </div>
                            <div className="row g-4">
                                {images.map((img, i) => (
                                    <div className="col-md-4" data-reveal="fade-up" data-reveal-delay={(i % 3 + 1) * 100} key={i}>
                                        <div className="card hover-lift bg-white h-100 border-0 rounded-4">
                                            <div className="portfolio-img-wrapper rounded-top-4">
                                                <ImgOrFallback src={imgUrl(img.link)} alt={img.title || `Highlight ${i + 1}`} icon="bi-images" />
                                            </div>
                                            <div className="card-body p-4">
                                                {img.type && <span className="badge bg-light text-dark mb-2">{img.type}</span>}
                                                <h5 className="fw-bold mb-0">{img.title || "Highlight"}</h5>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* ABOUT */}
                {d.aboutme && (
                    <section id="about" className="py-5 bg-white">
                        <div className="container py-4">
                            <div className="row gx-lg-5 align-items-center">
                                <div className="col-lg-5 mb-4 mb-lg-0" data-reveal="fade-right">
                                    <div className={`about-photo ${hasPhoto ? "" : "about-photo-ph"}`}>
                                        {hasPhoto ? (
                                            <img src={photo} alt={d.name || "Profile"} loading="lazy" onError={() => setImgOk(false)} className="img-fluid rounded-4" />
                                        ) : (
                                            <span aria-hidden="true">{initials}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="col-lg-7" data-reveal="fade-left">
                                    <h2 className="section-title mb-4">About {d.name}</h2>
                                    <p className="lead text-muted mb-4">{d.aboutme}</p>
                                    <div className="row mt-4 mb-1">
                                        <div className="col-sm-6 mb-3">
                                            <h3 className="fw-bold pw-stat mb-0">{d.experience || "—"}</h3>
                                            <span className="text-muted fw-semibold">Experience</span>
                                        </div>
                                        {langCount > 0 && (
                                            <div className="col-sm-6 mb-3">
                                                <h3 className="fw-bold pw-stat mb-0">{langCount}</h3>
                                                <span className="text-muted fw-semibold">Languages spoken</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="d-flex flex-wrap gap-2">
                                        {d.addresses && <span className="chip"><i className="bi bi-geo-alt-fill me-1" aria-hidden="true"></i>{d.addresses}</span>}
                                        {d.languages && <span className="chip"><i className="bi bi-translate me-1" aria-hidden="true"></i>{d.languages}</span>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* WHAT I DO */}
                {SERVICES.length > 0 && (
                    <section id="services" className="py-5">
                        <div className="container py-4">
                            <h2 className="section-title mb-5" data-reveal="fade-up">My focus areas</h2>
                            <div className="row g-4">
                                {SERVICES.map((s, i) => (
                                    <div className="col-md-4" data-reveal="fade-up" data-reveal-delay={(i + 1) * 100} key={i}>
                                        <div className="card bg-white border-0 p-4 h-100 hover-lift rounded-4">
                                            <div className="pw-service-icon mb-3"><i className={`bi ${s.icon} fs-3`} aria-hidden="true"></i></div>
                                            <h4 className="fw-bold mb-3">{s.t}</h4>
                                            <p className="text-muted mb-0">{s.d}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* REVIEWS */}
                {TESTIMONIALS.length > 0 && (
                    <section id="reviews" className="py-5 bg-white">
                        <div className="container py-4 border-top">
                            <h2 className="section-title mb-5" data-reveal="fade-up">What people say</h2>
                            <div className="row g-4">
                                {TESTIMONIALS.map((t, i) => (
                                    <div className="col-md-6" data-reveal="fade-up" data-reveal-delay={i * 100} key={i}>
                                        <div className="p-4 pw-card-flat rounded-4 h-100">
                                            <p className="fs-5 fst-italic">&quot;{t.q}&quot;</p>
                                            <h6 className="fw-bold mt-4 mb-0">{t.n}</h6>
                                            <small className="text-muted">{t.r}</small>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* CONTACT */}
                <section id="contact" className="py-5 bg-white">
                    <div className="container py-4 border-top">
                        <div className="row g-5">
                            <div className="col-lg-5" data-reveal="fade-right">
                                <h2 className="section-title mb-4">Get in touch</h2>
                                <p className="text-muted mb-5">Reach out to connect, collaborate or simply say hello. I usually respond within a few hours on business days.</p>
                                {!d.addresses && !d.email && !d.mobile && !d.whatsapp ? (
                                    <SiteEmptyState icon="bi-person-lines-fill" title="No contact details yet" message="Contact details will appear here once added." />
                                ) : (
                                    <ul className="list-unstyled mb-0">
                                        {d.addresses && (
                                            <li className="d-flex mb-4 align-items-center">
                                                <div className="pw-contact-ic"><i className="bi bi-geo-alt-fill fs-5" aria-hidden="true"></i></div>
                                                <div><h6 className="fw-bold mb-1">Location</h6><span className="text-muted">{d.addresses}</span></div>
                                            </li>
                                        )}
                                        {d.email && (
                                            <li className="d-flex mb-4 align-items-center">
                                                <div className="pw-contact-ic"><i className="bi bi-envelope-fill fs-5" aria-hidden="true"></i></div>
                                                <div><h6 className="fw-bold mb-1">Email</h6><a href={`mailto:${d.email}`} className="text-muted text-decoration-none">{d.email}</a></div>
                                            </li>
                                        )}
                                        {d.mobile && (
                                            <li className="d-flex mb-4 align-items-center">
                                                <div className="pw-contact-ic"><i className="bi bi-telephone-fill fs-5" aria-hidden="true"></i></div>
                                                <div><h6 className="fw-bold mb-1">Call</h6><a href={`tel:${d.mobile}`} className="text-muted text-decoration-none fw-bold">{d.mobile}</a></div>
                                            </li>
                                        )}
                                        {d.whatsapp && (
                                            <li className="d-flex align-items-center">
                                                <div className="pw-contact-ic pw-contact-ic-wa"><i className="bi bi-whatsapp fs-5" aria-hidden="true"></i></div>
                                                <div><h6 className="fw-bold mb-1">WhatsApp</h6><a href={`https://wa.me/${d.whatsapp}`} target="_blank" rel="noreferrer" className="text-muted text-decoration-none fw-bold">{d.whatsapp}</a></div>
                                            </li>
                                        )}
                                    </ul>
                                )}
                            </div>
                            <div className="col-lg-7" data-reveal="fade-left">
                                <div className="card border-0 rounded-4 pw-card-flat p-4 p-lg-5">
                                    <h4 className="fw-bold mb-4">Send a message</h4>
                                    <form onSubmit={handleContact} aria-label="Contact form">
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold" htmlFor="pw-cname">Full name</label>
                                                <input id="pw-cname" type="text" className="form-control form-control-lg" value={cName} onChange={(e) => setCName(e.target.value)} required />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold" htmlFor="pw-cphone">Phone number</label>
                                                <input id="pw-cphone" type="tel" className="form-control form-control-lg" value={cPhone} onChange={(e) => setCPhone(e.target.value)} required />
                                            </div>
                                            <div className="col-12">
                                                <label className="form-label fw-semibold" htmlFor="pw-cemail">Email address</label>
                                                <input id="pw-cemail" type="email" className="form-control form-control-lg" value={cEmail} onChange={(e) => setCEmail(e.target.value)} required />
                                            </div>
                                            <div className="col-12">
                                                <label className="form-label fw-semibold" htmlFor="pw-cmsg">Message</label>
                                                <textarea id="pw-cmsg" className="form-control form-control-lg" rows="4" value={cMsg} onChange={(e) => setCMsg(e.target.value)} required></textarea>
                                            </div>
                                            <div className="col-12 mt-4">
                                                <button type="submit" className="btn pw-btn-accent btn-lg w-100 fw-bold rounded-pill">Send message</button>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            {/* FOOTER */}
            <footer className="pw-footer py-5 mt-5">
                <div className="container py-4">
                    <div className="row g-5">
                        <div className="col-lg-5 col-md-6">
                            <span className="fw-bold fs-4 mb-3 d-block">{d.name || "My Website"}</span>
                            <p className="pw-footer-muted mb-4">{d.slogan || d.aboutme}</p>
                            <div className="d-flex gap-3 flex-wrap">
                                {d.mobile && <a href={`tel:${d.mobile}`} className="pw-social-ic" aria-label="Call"><i className="bi bi-telephone-fill" aria-hidden="true"></i></a>}
                                {d.whatsapp && <a href={`https://wa.me/${d.whatsapp}`} target="_blank" rel="noreferrer" className="pw-social-ic" aria-label="WhatsApp"><i className="bi bi-whatsapp" aria-hidden="true"></i></a>}
                                {d.email && <a href={`mailto:${d.email}`} className="pw-social-ic" aria-label="Email"><i className="bi bi-envelope-fill" aria-hidden="true"></i></a>}
                            </div>
                        </div>
                        <div className="col-lg-3 col-md-6">
                            <h5 className="fw-bold mb-4">Quick links</h5>
                            <ul className="list-unstyled mb-0">
                                <li className="mb-2"><a href="#portfolio" className="pw-footer-link">Portfolio</a></li>
                                <li className="mb-2"><a href="#about" className="pw-footer-link">About</a></li>
                                <li className="mb-2"><a href="#services" className="pw-footer-link">What I Do</a></li>
                                <li className="mb-2"><a href="#reviews" className="pw-footer-link">Reviews</a></li>
                            </ul>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <h5 className="fw-bold mb-4">Focus areas</h5>
                            <ul className="list-unstyled mb-0">
                                {SERVICES.map((s, i) => (
                                    <li className="mb-2" key={i}><a href="#services" className="pw-footer-link">{s.t}</a></li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <hr className="pw-footer-rule my-5" />
                    <div className="row align-items-center">
                        <div className="col-md-6 text-center text-md-start mb-3 mb-md-0">
                            <p className="pw-footer-muted small mb-0">&copy; {new Date().getFullYear()} {d.name || "My Website"}. All rights reserved.</p>
                        </div>
                        <div className="col-md-6 text-center text-md-end">
                            <p className="pw-footer-muted small mb-0">Powered by <span className="pw-footer-brand fw-semibold">Dial Nepal</span></p>
                        </div>
                    </div>
                </div>
            </footer>

            {/* THEME — crimson + deep indigo, grounded in the Dial Nepal / personal-profile brief */}
            <style>{`
                .pw{
                    --pw-ink:#1f2328; --pw-indigo:#14213d; --pw-indigo-d:#0c1526; --pw-muted:#666f7a;
                    --pw-bg:#fbfaf7; --pw-bg-2:#f1eee6; --pw-crimson:#b7233a; --pw-crimson-d:#8f1b2d; --pw-gold:#c99a2e;
                    font-family:'Inter',sans-serif; color:var(--pw-ink); background:var(--pw-bg);
                }
                .pw h1,.pw h2,.pw h3,.pw h4,.pw .navbar-brand{ font-family:'Sora',sans-serif; }
                .pw *:focus-visible{ outline:3px solid var(--pw-gold); outline-offset:2px; border-radius:4px; }
                .pw a, .pw button{ transition:background-color .2s ease, color .2s ease, transform .2s ease; }

                .pw-skip-link{ position:absolute; left:-9999px; top:auto; z-index:2000; background:var(--pw-indigo-d); color:#fff; padding:10px 18px; border-radius:0 0 8px 0; }
                .pw-skip-link:focus{ left:0; top:0; }

                .pw .navbar{ background-color:transparent; border-bottom:1px solid transparent; transition:background-color .25s ease, border-color .25s ease; }
                .pw .navbar .navbar-brand, .pw .navbar .nav-link{ color:#fff; }
                .pw .navbar.pw-nav-scrolled{ background-color:var(--pw-bg) !important; border-bottom:1px solid #e7e2d4; }
                .pw .navbar.pw-nav-scrolled .navbar-brand, .pw .navbar.pw-nav-scrolled .nav-link{ color:var(--pw-ink); }
                .pw .nav-link:hover, .pw .nav-link:focus-visible{ color:var(--pw-crimson) !important; }

                .pw-btn-accent{ background:var(--pw-crimson); border:1px solid var(--pw-crimson); color:#fff; }
                .pw-btn-accent:hover{ background:var(--pw-crimson-d); border-color:var(--pw-crimson-d); color:#fff; }
                .pw-btn-outline{ background:transparent; border:2px solid #fff; color:#fff; }
                .pw-btn-outline:hover{ background:#fff; color:var(--pw-indigo); }
                .pw .navbar.pw-nav-scrolled .pw-btn-outline{ border-color:var(--pw-indigo); color:var(--pw-indigo); }
                .pw .navbar.pw-nav-scrolled .pw-btn-outline:hover{ background:var(--pw-indigo); color:#fff; }

                /* HERO */
                .pw .hero-section{
                    background-color:var(--pw-indigo); background-size:cover; background-position:center;
                    color:#fff; padding:150px 0 100px 0;
                }
                .pw-badge{ display:inline-block; background:rgba(255,255,255,.12); border:1px solid rgba(255,255,255,.35); color:#fff; font-weight:600; font-size:.85rem; padding:6px 16px; border-radius:50px; }
                .pw-role{ color:rgba(255,255,255,.75); font-weight:500; }
                .pw-lead{ color:rgba(255,255,255,.85); }
                .pw .hero-photo{ width:260px; height:260px; border-radius:50%; overflow:hidden; border:4px solid rgba(255,255,255,.25); background:var(--pw-crimson); }
                .pw .hero-photo img{ width:100%; height:100%; object-fit:cover; }
                .pw .hero-photo-ph{ display:flex; align-items:center; justify-content:center; }
                .pw .hero-photo-ph span{ font-family:'Sora',sans-serif; font-size:88px; font-weight:800; color:#fff; }
                .pw .hero-exp{ color:rgba(255,255,255,.75); font-weight:600; }
                @media(max-width:991px){ .pw .hero-photo{ width:210px; height:210px; } }
                @media(max-width:575px){ .pw .hero-section{ padding:120px 0 70px 0; } .pw .hero-photo{ width:170px; height:170px; } }

                .pw .section-title{ font-weight:700; color:var(--pw-indigo); }

                .pw-img-fallback{
                    width:100%; height:100%; min-height:160px; display:flex; align-items:center; justify-content:center;
                    background:var(--pw-indigo); color:var(--pw-gold); font-size:2.2rem;
                }
                .pw .hover-lift{ transition:transform .25s ease, box-shadow .25s ease; box-shadow:0 2px 10px rgba(20,33,61,.06); }
                .pw .hover-lift:hover, .pw .hover-lift:focus-within{ transform:translateY(-4px); box-shadow:0 14px 26px rgba(20,33,61,.12) !important; }
                .pw .portfolio-img-wrapper{ overflow:hidden; height:240px; }
                .pw .portfolio-img-wrapper img{ width:100%; height:100%; object-fit:cover; }

                .pw .about-photo{ height:400px; border-radius:1rem; overflow:hidden; }
                .pw .about-photo img{ width:100%; height:100%; object-fit:cover; }
                .pw .about-photo-ph{ display:flex; align-items:center; justify-content:center; background:var(--pw-indigo); color:#fff; }
                .pw .about-photo-ph span{ font-family:'Sora',sans-serif; font-size:100px; font-weight:800; }
                .pw-stat{ color:var(--pw-crimson); }

                .pw .chip{ background:var(--pw-bg-2); color:var(--pw-indigo); font-weight:600; padding:8px 16px; border-radius:50px; display:inline-block; font-size:.9rem; }

                .pw-service-icon{ width:52px; height:52px; border-radius:12px; background:var(--pw-bg-2); color:var(--pw-crimson); display:flex; align-items:center; justify-content:center; }
                .pw-card-flat{ background:var(--pw-bg-2); }

                .pw .pw-contact-ic{ background:var(--pw-bg-2); color:var(--pw-crimson); padding:1rem; border-radius:50%; margin-right:1rem; }
                .pw .pw-contact-ic-wa{ color:#1fae5b; }

                .pw .form-control{ transition:border-color .2s ease, box-shadow .2s ease; }
                .pw .form-control:focus, .pw .btn:focus-visible{ box-shadow:0 0 0 4px rgba(201,154,46,.25); border-color:var(--pw-gold); }

                .pw-footer{ background:var(--pw-indigo-d); color:#e7e9ee; }
                .pw-footer-muted{ color:#aab0bd; }
                .pw-footer-link{ color:#aab0bd; text-decoration:none; }
                .pw-footer-link:hover{ color:#fff; }
                .pw-footer-brand{ color:var(--pw-gold); }
                .pw-footer-rule{ border-color:rgba(255,255,255,.12); }
                .pw .pw-social-ic{ width:40px; height:40px; border-radius:50%; background:rgba(255,255,255,.08); color:#e7e9ee; display:inline-flex; align-items:center; justify-content:center; font-size:1rem; text-decoration:none; }
                .pw .pw-social-ic:hover{ background:var(--pw-crimson); color:#fff; }
            `}</style>
        </div>
    );
}